import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import NavigationBehaviorTreeSection, {
    DEFAULT_CATALOG,
    DEFAULT_LIMITS,
    stageHasValidEnding,
    STAGE_TABS,
} from "components/control/settings/NavigationBehaviorTreeSection";
import {
    deleteNav2BehaviorProfile,
    fetchNav2BehaviorProfile,
    fetchNav2BehaviorProfiles,
    fetchNav2NavStatus,
    fetchNav2OperatorParams,
    getNav2ParamsApiBaseUrl,
    navServiceDisplayState,
    postNav2BehaviorActivate,
    postNav2BehaviorProfile,
    postNav2BehaviorRename,
    postNav2NavRestart,
    putNav2BehaviorProfile,
    type Nav2BehaviorProfile,
    type Nav2BehaviorTreeCatalog,
    type Nav2BehaviorTreeLimits,
    type Nav2BehaviorTreeSettings,
    type NavServiceDisplayState,
} from "lib/nav2ParamsApi";
import { toUserFacingApiError } from "lib/utils/userFacingApiError";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import useFleetArcId from "lib/utils/useFleetArcId";
import { useARC } from "providers/ARC/ARCProvider";
import { useToast } from "providers/ToastProvider";

const settingsEqual = (
    a: Nav2BehaviorTreeSettings | null,
    b: Nav2BehaviorTreeSettings | null
) => JSON.stringify(a) === JSON.stringify(b);

export type NavigationBehaviourSettingsVariant = "page" | "devPopup";

type NavigationBehaviourSettingsPanelProps = {
    variant?: NavigationBehaviourSettingsVariant;
};

const NavigationBehaviourSettingsPanel: React.FC<
    NavigationBehaviourSettingsPanelProps
> = ({ variant = "page" }) => {
    const isDevPopup = variant === "devPopup";
    const { t } = useTranslation(["settings", "common", "control"]);
    const { addToast } = useToast();
    const { selectedARC } = useARC();
    const isSecure = useIsHttpSecure();
    const arcId = useFleetArcId();

    const baseUrl = useMemo(
        () => getNav2ParamsApiBaseUrl(selectedARC?.host ?? null, isSecure, arcId),
        [selectedARC?.host, isSecure, arcId]
    );

    const [robotProfileName, setRobotProfileName] = useState<string | null>(null);
    const [profiles, setProfiles] = useState<Nav2BehaviorProfile[]>([]);
    const [selectedName, setSelectedName] = useState("");
    const [activeName, setActiveName] = useState<string | null>(null);
    const [settings, setSettings] = useState<Nav2BehaviorTreeSettings | null>(null);
    const [savedSettings, setSavedSettings] = useState<Nav2BehaviorTreeSettings | null>(
        null
    );
    const [limits, setLimits] = useState<Nav2BehaviorTreeLimits>(DEFAULT_LIMITS);
    const [catalog, setCatalog] = useState<Nav2BehaviorTreeCatalog>(DEFAULT_CATALOG);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [invalidFlow, setInvalidFlow] = useState(false);
    const [navServiceState, setNavServiceState] =
        useState<NavServiceDisplayState>("unknown");
    const [profilesAvailable, setProfilesAvailable] = useState(true);

    const loadToken = useRef(0);
    const baseUrlRef = useRef(baseUrl);
    baseUrlRef.current = baseUrl;

    const dirty = !settingsEqual(settings, savedSettings);
    const isActiveSelection =
        Boolean(selectedName) && activeName !== null && selectedName === activeName;

    const refreshNavStatus = useCallback(async () => {
        const url = baseUrlRef.current;
        if (!url) return;
        try {
            const status = await fetchNav2NavStatus(url);
            setNavServiceState(navServiceDisplayState(status));
        } catch {
            setNavServiceState((prev) => (prev === "unknown" ? "unknown" : prev));
        }
    }, []);

    const loadProfile = useCallback(async (name: string) => {
        const url = baseUrlRef.current;
        if (!url || !name) return;
        const token = ++loadToken.current;
        setLoading(true);
        try {
            const res = await fetchNav2BehaviorProfile(url, name);
            if (token !== loadToken.current) return;
            setSettings(res.settings);
            setSavedSettings(res.settings);
            if (res.limits) setLimits({ ...DEFAULT_LIMITS, ...res.limits });
            if (res.catalog) setCatalog(res.catalog);
            setSelectedName(res.name);
            setProfilesAvailable(true);
        } catch (e) {
            if (token !== loadToken.current) return;
            setProfilesAvailable(false);
            addToast({
                type: "error",
                message: toUserFacingApiError(
                    e,
                    t("navigationBehaviour.profiles.loadFailed", {
                        defaultValue: "Failed to load behaviour profile",
                    })
                ),
            });
        } finally {
            if (token === loadToken.current) setLoading(false);
        }
    }, [addToast, t]);

    const reloadAll = useCallback(async () => {
        const url = baseUrlRef.current;
        if (!url) {
            setProfiles([]);
            setSelectedName("");
            setActiveName(null);
            setSettings(null);
            setSavedSettings(null);
            setRobotProfileName(null);
            return;
        }
        setLoading(true);
        try {
            const [listRes, operatorRes] = await Promise.all([
                fetchNav2BehaviorProfiles(url),
                fetchNav2OperatorParams(url).catch(() => null),
            ]);
            setProfilesAvailable(true);
            setProfiles(listRes.profiles);
            setActiveName(listRes.active);
            setRobotProfileName(operatorRes?.profile_name ?? null);
            const pick =
                listRes.active &&
                listRes.profiles.some((p) => p.name === listRes.active)
                    ? listRes.active
                    : listRes.profiles[0]?.name ?? "";
            if (pick) {
                await loadProfile(pick);
            } else {
                setSelectedName("");
                setSettings(null);
                setSavedSettings(null);
            }
            await refreshNavStatus();
        } catch (e) {
            setProfilesAvailable(false);
            addToast({
                type: "error",
                message: toUserFacingApiError(
                    e,
                    t("navigationBehaviour.profiles.loadFailed", {
                        defaultValue: "Failed to load behaviour profiles",
                    })
                ),
            });
        } finally {
            setLoading(false);
        }
    }, [addToast, loadProfile, refreshNavStatus, t]);

    useEffect(() => {
        void reloadAll();
    }, [reloadAll]);

    useEffect(() => {
        if (navServiceState !== "starting" || !baseUrl) return;
        const id = window.setInterval(() => {
            void refreshNavStatus();
        }, 2000);
        return () => window.clearInterval(id);
    }, [navServiceState, baseUrl, refreshNavStatus]);

    const handleSelectProfile = useCallback(
        (next: string) => {
            if (next === selectedName) return;
            if (
                dirty &&
                !window.confirm(
                    t("navigationBehaviour.profiles.discardConfirm", {
                        defaultValue:
                            "Discard unsaved behaviour changes and switch profile?",
                    })
                )
            ) {
                return;
            }
            void loadProfile(next);
        },
        [dirty, loadProfile, selectedName, t]
    );

    const handleSave = useCallback(async () => {
        const url = baseUrlRef.current;
        if (!url || !selectedName || !settings || busy || !dirty || invalidFlow) return;
        const invalid = STAGE_TABS.some(
            (name) => !stageHasValidEnding(name, settings[name])
        );
        if (invalid) return;
        setBusy(true);
        try {
            const res = await putNav2BehaviorProfile(url, selectedName, settings);
            setSettings(res.settings);
            setSavedSettings(res.settings);
            if (res.is_active) {
                setActiveName(res.name);
            }
            const list = await fetchNav2BehaviorProfiles(url);
            setProfiles(list.profiles);
            setActiveName(list.active);
            addToast({
                type: "success",
                message:
                    res.message ??
                    t("navigationBehaviour.profiles.saved", {
                        defaultValue: "Behaviour profile saved",
                    }),
            });
            await refreshNavStatus();
        } catch (e) {
            addToast({
                type: "error",
                message: toUserFacingApiError(
                    e,
                    t("navigationBehaviour.profiles.saveFailed", {
                        defaultValue: "Failed to save behaviour profile",
                    })
                ),
            });
        } finally {
            setBusy(false);
        }
    }, [addToast, busy, dirty, invalidFlow, refreshNavStatus, selectedName, settings, t]);

    const handleSaveAs = useCallback(async () => {
        const url = baseUrlRef.current;
        if (!url || !settings || busy) return;
        const name = window.prompt(
            t("navigationBehaviour.profiles.saveAsPrompt", {
                defaultValue: "New behaviour profile name",
            }),
            ""
        );
        if (!name?.trim()) return;
        setBusy(true);
        try {
            const res = await postNav2BehaviorProfile(url, name.trim(), settings);
            const list = await fetchNav2BehaviorProfiles(url);
            setProfiles(list.profiles);
            setActiveName(list.active);
            setSelectedName(res.name);
            setSettings(res.settings);
            setSavedSettings(res.settings);
            addToast({
                type: "success",
                message:
                    res.message ??
                    t("navigationBehaviour.profiles.created", {
                        defaultValue: "Behaviour profile created",
                    }),
            });
        } catch (e) {
            addToast({
                type: "error",
                message: toUserFacingApiError(
                    e,
                    t("navigationBehaviour.profiles.saveAsFailed", {
                        defaultValue: "Failed to create behaviour profile",
                    })
                ),
            });
        } finally {
            setBusy(false);
        }
    }, [addToast, busy, settings, t]);

    const handleActivate = useCallback(async () => {
        const url = baseUrlRef.current;
        if (!url || !selectedName || busy || isActiveSelection) return;
        if (
            dirty &&
            !window.confirm(
                t("navigationBehaviour.profiles.activateDirtyConfirm", {
                    defaultValue:
                        "You have unsaved changes. Activate the saved profile from disk?",
                })
            )
        ) {
            return;
        }
        if (
            !window.confirm(
                t("navigationBehaviour.profiles.activateConfirm", {
                    name: selectedName,
                    defaultValue: `Activate behaviour profile '${selectedName}'? Navigation is not restarted.`,
                })
            )
        ) {
            return;
        }
        setBusy(true);
        try {
            const res = await postNav2BehaviorActivate(url, selectedName);
            setSettings(res.settings);
            setSavedSettings(res.settings);
            setActiveName(res.behavior_profile_name ?? selectedName);
            const list = await fetchNav2BehaviorProfiles(url);
            setProfiles(list.profiles);
            setActiveName(list.active);
            addToast({
                type: "success",
                message:
                    res.message ??
                    t("navigationBehaviour.profiles.activated", {
                        defaultValue: "Behaviour profile activated",
                    }),
            });
            await refreshNavStatus();
        } catch (e) {
            addToast({
                type: "error",
                message: toUserFacingApiError(
                    e,
                    t("navigationBehaviour.profiles.activateFailed", {
                        defaultValue: "Failed to activate behaviour profile",
                    })
                ),
            });
        } finally {
            setBusy(false);
        }
    }, [
        addToast,
        busy,
        dirty,
        isActiveSelection,
        refreshNavStatus,
        selectedName,
        t,
    ]);

    const handleRename = useCallback(async () => {
        const url = baseUrlRef.current;
        if (!url || !selectedName || busy || isActiveSelection) return;
        const next = window.prompt(
            t("navigationBehaviour.profiles.renamePrompt", {
                defaultValue: "Rename behaviour profile to",
            }),
            selectedName
        );
        if (!next?.trim() || next.trim() === selectedName) return;
        setBusy(true);
        try {
            const res = await postNav2BehaviorRename(url, selectedName, next.trim());
            const newName = res.name ?? next.trim();
            const list = await fetchNav2BehaviorProfiles(url);
            setProfiles(list.profiles);
            setActiveName(list.active);
            setSelectedName(newName);
            addToast({
                type: "success",
                message: t("navigationBehaviour.profiles.renamed", {
                    defaultValue: "Behaviour profile renamed",
                }),
            });
        } catch (e) {
            addToast({
                type: "error",
                message: toUserFacingApiError(
                    e,
                    t("navigationBehaviour.profiles.renameFailed", {
                        defaultValue: "Failed to rename behaviour profile",
                    })
                ),
            });
        } finally {
            setBusy(false);
        }
    }, [addToast, busy, isActiveSelection, selectedName, t]);

    const handleDelete = useCallback(async () => {
        const url = baseUrlRef.current;
        if (!url || !selectedName || busy || isActiveSelection) return;
        if (
            !window.confirm(
                t("navigationBehaviour.profiles.deleteConfirm", {
                    name: selectedName,
                    defaultValue: `Delete behaviour profile '${selectedName}'?`,
                })
            )
        ) {
            return;
        }
        setBusy(true);
        try {
            await deleteNav2BehaviorProfile(url, selectedName);
            addToast({
                type: "success",
                message: t("navigationBehaviour.profiles.deleted", {
                    defaultValue: "Behaviour profile deleted",
                }),
            });
            await reloadAll();
        } catch (e) {
            addToast({
                type: "error",
                message: toUserFacingApiError(
                    e,
                    t("navigationBehaviour.profiles.deleteFailed", {
                        defaultValue: "Failed to delete behaviour profile",
                    })
                ),
            });
        } finally {
            setBusy(false);
        }
    }, [addToast, busy, isActiveSelection, reloadAll, selectedName, t]);

    const handleRestartNav = useCallback(async () => {
        const url = baseUrlRef.current;
        if (!url || busy) return;
        if (
            !window.confirm(
                t("navigationBehaviour.profiles.restartConfirm", {
                    defaultValue:
                        "Restart nav.service? Navigation will stop briefly and behaviour changes will take effect.",
                })
            )
        ) {
            return;
        }
        setBusy(true);
        try {
            await postNav2NavRestart(url);
            setNavServiceState("starting");
            addToast({
                type: "success",
                message: t("navigationBehaviour.profiles.restartScheduled", {
                    defaultValue: "nav.service restart scheduled",
                }),
            });
            for (let i = 0; i < 90; i += 1) {
                await new Promise((r) => window.setTimeout(r, 2000));
                try {
                    const status = await fetchNav2NavStatus(url);
                    const state = navServiceDisplayState(status);
                    setNavServiceState(state);
                    if (state === "running" || state === "stopped") break;
                } catch {
                    /* keep polling */
                }
            }
        } catch (e) {
            addToast({
                type: "error",
                message: toUserFacingApiError(
                    e,
                    t("navigationBehaviour.profiles.restartFailed", {
                        defaultValue: "Failed to restart nav.service",
                    })
                ),
            });
        } finally {
            setBusy(false);
        }
    }, [addToast, busy, t]);

    const pageSubtitle = useMemo(() => {
        if (!baseUrl) {
            return t("navigationBehaviour.connectFirst", {
                defaultValue: "Connect to your ARC first to load behaviour settings.",
            });
        }
        return t("navigationBehaviour.description", {
            defaultValue:
                "Configure Navigate To Pose failure handling: planner, controller, and recovery fallback flows. Save a behaviour profile, activate it, then restart navigation to apply.",
        });
    }, [baseUrl, t]);

    const navStatusLabel =
        navServiceState === "running"
            ? t("navigationBehaviour.profiles.navRunning", { defaultValue: "nav running" })
            : navServiceState === "starting"
              ? t("navigationBehaviour.profiles.navStarting", {
                    defaultValue: "nav starting…",
                })
              : navServiceState === "stopped"
                ? t("navigationBehaviour.profiles.navStopped", {
                      defaultValue: "nav stopped",
                  })
                : t("navigationBehaviour.profiles.navUnknown", {
                      defaultValue: "nav status unknown",
                  });

    const controlsDisabled = !baseUrl || loading || busy || !profilesAvailable;

    const btnClass = isDevPopup
        ? "rounded-r5 border border-black70 bg-black90 px-2 py-0.5 text-h5 text-white hover:bg-black80 disabled:opacity-50"
        : "rounded-r5 border border-black70 bg-black100 px-3 py-1 text-h5 text-white hover:bg-black80 disabled:opacity-50";
    const btnPrimaryClass = isDevPopup
        ? "rounded-r5 border border-primary2 bg-primary2 px-2 py-0.5 text-h5 font-semibold text-white hover:bg-primary1 disabled:opacity-50"
        : "rounded-r5 border border-primary2 bg-primary2 px-3 py-1 text-h5 font-semibold text-white hover:bg-primary1 disabled:opacity-50";
    const btnDangerClass = isDevPopup
        ? "rounded-r5 border border-warning bg-black90 px-2 py-0.5 text-h5 font-semibold text-warning hover:bg-black80 disabled:opacity-50"
        : "rounded-r5 border border-warning bg-black100 px-3 py-1 text-h5 font-semibold text-warning hover:bg-black80 disabled:opacity-50";

    return (
        <div
            className={
                isDevPopup
                    ? "flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto bg-black100 p-3"
                    : "flex max-w-3xl flex-col gap-6"
            }
        >
            <header>
                {isDevPopup ? (
                    <>
                        <p className="text-h5 font-semibold text-white">
                            {t("control:devNav.behaviorConfigTitle", {
                                defaultValue: "Navigate To Pose behaviour",
                            })}
                        </p>
                        <p className="mt-0.5 text-[11px] text-black40">
                            {t("control:devNav.behaviorConfigDescription", {
                                defaultValue:
                                    "Edit behaviour profiles, activate live XML, then restart nav.service.",
                            })}
                        </p>
                    </>
                ) : (
                    <>
                        <h1 className="text-h4 font-semibold text-white">
                            {t("navigationBehaviour.title")}
                        </h1>
                        <p className="mt-1 text-h5 text-black40">{pageSubtitle}</p>
                    </>
                )}
                {robotProfileName ? (
                    <p
                        className={`inline-flex items-center gap-2 text-h5 text-black40 ${
                            isDevPopup ? "mt-2" : "mt-2"
                        }`}
                    >
                        <span className="h-2 w-2 shrink-0 rounded-full bg-primary2" aria-hidden />
                        {t("navigationBehaviour.activeProfile", {
                            profile: robotProfileName,
                            defaultValue: `Active robot profile: {{profile}}`,
                        })}
                    </p>
                ) : null}
            </header>

            <section
                className={
                    isDevPopup
                        ? "rounded-r5 border border-black70 bg-black90 p-3"
                        : "rounded-r10 border border-black80 bg-black90 p-4"
                }
            >
                <div className="flex flex-wrap items-end gap-3">
                    <label className="flex min-w-[10rem] flex-1 flex-col gap-1">
                        <span
                            className={
                                isDevPopup
                                    ? "text-[11px] font-medium uppercase tracking-wide text-black40"
                                    : "text-h5 font-semibold text-white"
                            }
                        >
                            {t("navigationBehaviour.profiles.label", {
                                defaultValue: "Behaviour profile",
                            })}
                        </span>
                        <select
                            className={
                                isDevPopup
                                    ? "rounded-r5 border border-black70 bg-black100 px-2 py-1.5 text-h5 text-white outline-none focus:border-primary2 disabled:opacity-50"
                                    : "min-h-[44px] rounded-r5 border border-black60 bg-black100 px-3 py-2 text-h5 text-white outline-none focus:border-primary2 disabled:opacity-50"
                            }
                            value={selectedName}
                            disabled={controlsDisabled || profiles.length === 0}
                            onChange={(e) => handleSelectProfile(e.target.value)}
                        >
                            {profiles.length === 0 ? (
                                <option value="">
                                    {t("navigationBehaviour.profiles.empty", {
                                        defaultValue: "No profiles",
                                    })}
                                </option>
                            ) : (
                                profiles.map((p) => (
                                    <option key={p.name} value={p.name}>
                                        {p.name}
                                        {p.is_active || p.name === activeName
                                            ? ` (${t("navigationBehaviour.profiles.activeMarker", {
                                                  defaultValue: "active",
                                              })})`
                                            : ""}
                                    </option>
                                ))
                            )}
                        </select>
                    </label>
                    <p
                        className={
                            isDevPopup
                                ? "pb-1 text-[11px] text-black40"
                                : "pb-2 text-h5 text-black40"
                        }
                    >
                        {navStatusLabel}
                    </p>
                </div>

                {activeName ? (
                    <p className="mt-2 text-[11px] text-black50">
                        {t("control:devNav.activeBehaviorProfile", {
                            name: activeName,
                            defaultValue: "Live BT profile: {{name}}",
                        })}
                    </p>
                ) : null}

                <div className="mt-3 flex flex-wrap gap-2">
                    <button
                        type="button"
                        disabled={
                            controlsDisabled || !selectedName || !dirty || invalidFlow
                        }
                        onClick={() => void handleSave()}
                        className={btnPrimaryClass}
                    >
                        {t("navigationBehaviour.profiles.save", {
                            defaultValue: "Save",
                        })}
                    </button>
                    <button
                        type="button"
                        disabled={controlsDisabled || !settings}
                        onClick={() => void handleSaveAs()}
                        className={btnClass}
                    >
                        {t("navigationBehaviour.profiles.saveAs", {
                            defaultValue: "Save as…",
                        })}
                    </button>
                    <button
                        type="button"
                        disabled={controlsDisabled || !selectedName || isActiveSelection}
                        onClick={() => void handleActivate()}
                        className={btnClass}
                    >
                        {t("navigationBehaviour.profiles.activate", {
                            defaultValue: "Activate",
                        })}
                    </button>
                    <button
                        type="button"
                        disabled={controlsDisabled || !selectedName || isActiveSelection}
                        onClick={() => void handleRename()}
                        className={btnClass}
                    >
                        {t("navigationBehaviour.profiles.rename", {
                            defaultValue: "Rename…",
                        })}
                    </button>
                    <button
                        type="button"
                        disabled={controlsDisabled || !selectedName || isActiveSelection}
                        onClick={() => void handleDelete()}
                        className={btnDangerClass}
                    >
                        {t("navigationBehaviour.profiles.delete", {
                            defaultValue: "Delete",
                        })}
                    </button>
                    <button
                        type="button"
                        disabled={!baseUrl || busy || loading}
                        onClick={() => void handleRestartNav()}
                        className={`${btnDangerClass}${isDevPopup ? "" : " ml-auto"}`}
                    >
                        {t("navigationBehaviour.profiles.restart", {
                            defaultValue: "Restart nav.service",
                        })}
                    </button>
                </div>
                {!isDevPopup ? (
                    <p className="mt-2 text-h5 text-black60">
                        {t("navigation.behaviorTree.restartHint", {
                            defaultValue:
                                "These changes require a navigation restart to take effect.",
                        })}
                    </p>
                ) : null}
            </section>

            <section
                className={
                    isDevPopup
                        ? "rounded-r5 border border-black70 bg-black90 p-3"
                        : "rounded-r10 border border-black80 bg-black90 p-4"
                }
            >
                <NavigationBehaviorTreeSection
                    baseUrl={baseUrl}
                    disabled={!baseUrl || busy}
                    hideChrome
                    settings={settings}
                    onSettingsChange={setSettings}
                    dirty={dirty}
                    limits={limits}
                    catalog={catalog}
                    loading={loading}
                    onRefresh={() => {
                        if (selectedName) void loadProfile(selectedName);
                        else void reloadAll();
                    }}
                    onInvalidChange={setInvalidFlow}
                />
            </section>
        </div>
    );
};

export default NavigationBehaviourSettingsPanel;
